// supervisor-post-route-worktree-reset.ts
//
// Phase 06d R7 (dogfood F-17, spec E20). `recoverOwnedCheckout` already runs
// before EVERY dispatch (supervisor-pre-dispatch-checks.ts), so inside one
// `run` a refused attempt's diff never becomes the next attempt's baseline.
// The gap was BETWEEN two `run`s: the next `run` makes its bootstrap commit
// (staging ".", supervisor-mission-run.ts) before its first dispatch, so the
// leftover diff of a non-done attempt landed in a `[T-0000]` commit. Bootstrap
// itself stays — it is how a human's edit between two runs (BLUEPRINT…) gets
// committed — so the worktree is reset right after the attempt instead.
//
// One rule (B2, amended 06g R2a/B8, review 06g H1): reset after EVERY route
// except verdict `recovering` (HEAD moved, group not reaped — §9.4 hands-off).
// `done` resets too: its commit holds every owned file and the verifier
// already refused anything outside scope, so what the reset removes is only
// what the recipe left behind AFTER the inventory diff (coverage output,
// caches, a repo without `.gitignore`) — untracked junk the next `run` would
// otherwise refuse as fail-closed `worktree_dirty`. `quota_sleep` ends the
// run with the ticket back at `todo`, so its half-done files are archived +
// reset like retry/blocked/escalate, needs_decision and contract_error.
//
// Order (B3): the caller runs this AFTER `finalizeAttemptAfterRoute`, so
// `attempt_finished` is already journaled. Archive first (B1), then reset; a
// failure in either is reported and swallowed — an archive failure skips THIS
// reset, but the next dispatch's pre-dispatch reset still runs as before and
// does not archive, so the operator line is the only warning (review L9).
// A successful reset is announced too (review M3): a human edit made while
// the attempt ran is gone from the worktree; its copy is in the forensics.
import type { GitRunner } from "./commit-owner.js";
import type { AttemptCycleResult } from "./supervisor-attempt-cycle.js";
import { archiveForensicsBeforeReset } from "./supervisor-forensics-archival.js";
import type { RouteOutcomeResult } from "./supervisor-outcome-router.js";
import { recoverOwnedCheckout } from "./supervisor-owned-checkout-recovery.js";

export interface ResetWorktreeAfterNonDoneRouteInput {
  cycle: AttemptCycleResult;
  route: RouteOutcomeResult;
  controlRoot: string;
  repoDir: string;
  gitRunner: GitRunner;
  ticketId: string;
  attemptNo: number;
  /** Operator warning line (the run log in production); undefined ⇒ process stderr. */
  notify?: (line: string) => void;
}

export function shouldResetWorktreeAfterRoute(cycle: AttemptCycleResult): boolean {
  return !(cycle.kind === "verified" && cycle.verdict.kind === "recovering");
}

/** Archives then resets the mission worktree after an attempt (every route but `recovering`). Never throws. */
export async function resetWorktreeAfterNonDoneRoute(
  input: ResetWorktreeAfterNonDoneRouteInput,
): Promise<void> {
  if (!shouldResetWorktreeAfterRoute(input.cycle)) return;
  try {
    await archiveForensicsBeforeReset({
      controlRoot: input.controlRoot,
      repoDir: input.repoDir,
      ticketId: input.ticketId,
      attemptNo: input.attemptNo,
      cycle: input.cycle,
      routeKind: input.route.kind,
    });
    await recoverOwnedCheckout(input.gitRunner, input.repoDir);
    const copy = `attempts/${input.ticketId}-${input.attemptNo}/forensics`;
    emit(
      input,
      `${input.ticketId}: đã reset worktree sau attempt ${input.attemptNo} (${routeLabel(input.route)}); bản sao thay đổi ở ${copy} (trong control root)`,
    );
  } catch (err) {
    const reason = err instanceof Error ? err.message : String(err);
    emit(
      input,
      `${input.ticketId}: không archive/reset được worktree sau attempt ${input.attemptNo} (${routeLabel(input.route)}) — ${reason}. Dispatch kế vẫn reset trước khi chạy; nếu dừng run bây giờ, kiểm tra worktree trước run sau.`,
    );
  }
}

/** 06e R9 (F-29): the notify line names the category the journal records. */
function routeLabel(route: RouteOutcomeResult): string {
  if (route.kind !== "retry_decision" || !route.decision.category) return route.kind;
  return `retry_decision: ${route.decision.category}`;
}

function emit(input: ResetWorktreeAfterNonDoneRouteInput, line: string): void {
  if (input.notify !== undefined) input.notify(line);
  else process.stderr.write(`${line}\n`);
}
